/**
 * @file `/catalog` — the full course list.
 * @module @labcraft/lab/ui/pages/catalog
 *
 * Umbrella #927 / part #940. The simple list of every installed course
 * (locked decision 4: no cross-course node graph, no `prereqs` on
 * `CourseCatalogEntry`). `/courses` is the *enrolled* view; this is
 * everything. A card navigates to that course's landing
 * (`/courses/:courseId`), where Parts render as a path.
 */

import { useEffect, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { useClient } from '@labcraft/core/frontend/react'
import { track } from '@labcraft/ui/hooks'
import type { Lab } from '../../client/index.ts'
import type { CourseCatalogEntry } from '../../shared/routes.ts'
import styles from './auth/auth.module.css'

export function CatalogPage(): JSX.Element {
  const app = useClient<Lab>()
  const navigate = useNavigate()
  const [courses, setCourses] = useState<readonly CourseCatalogEntry[]>([])

  useEffect(() => {
    let active = true
    app.courses
      .list()
      .then(entries => {
        if (active) setCourses(entries)
      })
      .catch((err: unknown) => {
        console.error('Failed to load course catalog', err)
        if (active) setCourses([])
      })
    return () => {
      active = false
    }
  }, [app])

  function openCourse(courseId: string): void {
    track('catalog_course_open', { courseId })
    navigate(`/courses/${courseId}`)
  }

  return (
    <main className={styles.main}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>Catalog</p>
        <h1 className={styles.title}>Every course.</h1>
        <p className={styles.lede}>
          Real shells, deliberate failure beats, and the judgment you can only
          build by trying things and watching them break.
        </p>

        {courses.length === 0 && (
          <p className={styles.fineprint}>
            No courses installed yet — check back soon.
          </p>
        )}

        <div className={styles.ssoStack}>
          {courses.map(course => (
            <button
              key={course.id}
              className={styles.ssoButton}
              type="button"
              onClick={() => openCourse(course.id)}>
              <span style={{ flex: 1, textAlign: 'left' }}>
                <strong>{course.title}</strong>
                <br />
                <span style={{ opacity: 0.7 }}>{course.tagline}</span>
              </span>
              <span aria-hidden>→</span>
            </button>
          ))}
        </div>
      </section>
    </main>
  )
}
